import React, { useContext, useMemo } from 'react';
import { Label } from '../../components/Label';
import { FileInput } from '../../components/File';
import { CustomField, WithCustomFields } from '../../../types/custom_field/customField';
import { FileEntity, FileEntityType } from '../../../types/file';
import { t } from '../../../types/translation/Translator';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import ImagesPreviewsGrid from '../ImagePreviewGrid/ImagePreviewGrid';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { FileContext } from '../../../context/FileContext';
import useMutation from '../../../hooks/useMutation';
import { DeleteFilesResponse, DeleteFilesVarables, FileMutations } from '../../../graphql/file.graphql';
import { useToast } from '../../../providers/ToastProvider';
import { ToastType } from '../Toast';

interface CustomFieldInputImageProps {
  entity: WithCustomFields;
  entityType: FileEntityType;
  disabled?: boolean;
  item: CustomField;
}

export default function CustomFieldInputImage({
  entity,
  entityType,
  disabled = false,
  item,
}: CustomFieldInputImageProps) {
  const { showToast } = useToast();
  const { files, setFiles } = useContext(FileContext);

  const { uploadFiles } = useFileUpload({
    entityId: (entity as any).id,
    entityType,
    customFieldId: item.id,
    setError: (msg: string) => {
      console.error('Error uploading files:', msg);
    },
  });

  const [remove] = useMutation<DeleteFilesResponse, DeleteFilesVarables>(FileMutations.remove, {
    onCompleted: result => {
      files.delete(result?.deleteFiles[0].id);
      setFiles(new Map(files));
    },
    onError: error => showToast({ type: ToastType.error, title: error.message }),
  });

  const handleFiles = async (filesToUpload: FileList | null) => {
    if (!filesToUpload) return;
    if (!filesToUpload.length) return;

    await uploadFiles(Array.from(filesToUpload));
  };

  const handleRemove = async (removeId: string) => {
    const file = files.get(removeId);
    if (!file) return;

    await remove({
      variables: {
        entityType,
        files: [{ id: file.id, version: file.version, companyId: file.companyId }] as FileEntity[],
      },
    });
  };

  const attachedImages = useMemo(() => {
    return [...files.values()].filter(file => file.path.includes((entity as any).id)).map(file => file.id);
  }, [files]);

  if (disabled) {
    if (!attachedImages?.length) return null;

    return (
      <>
        <p className='mt-1 text-sm font-medium text-slate-800'>{t(TranslationKey.image).plural.label}</p>
        <ImagesPreviewsGrid
          fileEntityType={entityType}
          attachedImages={attachedImages}
          handleRemove={handleRemove}
          disabled={disabled}
        />
      </>
    );
  }

  return (
    <div className='flex flex-col'>
      <Label label={t(TranslationKey.image).plural.label} mandatory={item.mandatory}>
        <FileInput multiple onFiles={handleFiles} acceptedTypes={'.jpg,.jpeg,.png'} />
      </Label>
      <div className='py-1'></div>
      <ImagesPreviewsGrid
        disabled={disabled}
        attachedImages={attachedImages}
        handleRemove={handleRemove}
        fileEntityType={entityType}
      />
    </div>
  );
}
